/**
 * AIWARI Realm Select screen (R232q5r).
 *
 * Heraldic dark fantasy UI for choosing realm before character creation.
 * 2 active realms + 1 reserved placeholder. State persisted in
 * localStorage via useUI store.
 */

import { useEffect, useState } from 'react';
import { REALMS, useUI, pseudoOnlineForRealm } from '../state/store.js';
import type { RealmConfig, RealmId } from '../state/store.js';

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?' +
  'family=Cinzel:wght@500;700;900&' +
  'family=Spectral+SC:wght@400;500;700&' +
  'family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&' +
  'family=JetBrains+Mono:wght@400;500&display=swap';

function ensureFonts() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[data-aiwari-fonts]')) return;
  const pre1 = document.createElement('link');
  pre1.rel = 'preconnect';
  pre1.href = 'https://fonts.googleapis.com';
  const pre2 = document.createElement('link');
  pre2.rel = 'preconnect';
  pre2.href = 'https://fonts.gstatic.com';
  pre2.crossOrigin = 'anonymous';
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONTS_HREF;
  link.setAttribute('data-aiwari-fonts', '');
  document.head.append(pre1, pre2, link);
}

function CornerOrnament({ flipX = false, flipY = false, size = 64 }: { flipX?: boolean; flipY?: boolean; size?: number }) {
  const transform = `${flipX ? 'scaleX(-1) ' : ''}${flipY ? 'scaleY(-1) ' : ''}`.trim();
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} style={{ transform: transform || undefined, position: 'absolute' }} aria-hidden>
      <defs>
        <linearGradient id="rsGoldGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8A6B2E" />
          <stop offset="50%" stopColor="#E8C77B" />
          <stop offset="100%" stopColor="#8A6B2E" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#rsGoldGrad)" strokeWidth="1.4" strokeLinecap="round">
        <path d="M0 1.5 L72 1.5" />
        <path d="M1.5 0 L1.5 72" />
        <path d="M1.5 24 L24 24 L24 1.5" opacity="0.65" />
        <path d="M10 10 Q22 10 22 22 Q22 34 10 34" />
        <circle cx="6" cy="6" r="1.3" fill="#E8C77B" stroke="none" />
        <circle cx="22" cy="22" r="1.1" fill="#C9A96A" stroke="none" />
      </g>
    </svg>
  );
}

export function RealmSelect() {
  useEffect(ensureFonts, []);
  const setStage = useUI((s) => s.setStage);
  const setRealm = useUI((s) => s.setRealm);
  const currentRealm = useUI((s) => s.realm);

  // R232q69: per-realm pseudo-online counter that ticks once per hour.
  // Re-evaluates every minute so the UI doesn't go stale on long sessions.
  const [onlineByRealm, setOnlineByRealm] = useState<Record<string, number>>(() =>
    Object.fromEntries(REALMS.map((r) => [r.id, pseudoOnlineForRealm(r)])),
  );
  useEffect(() => {
    const tick = () => setOnlineByRealm(
      Object.fromEntries(REALMS.map((r) => [r.id, pseudoOnlineForRealm(r)])),
    );
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);
  const totalOnline = Object.values(onlineByRealm).reduce((a, b) => a + b, 0);

  const onSelect = (id: RealmId) => {
    const realm = REALMS.find((r) => r.id === id);
    if (!realm || realm.status !== 'live') return;
    setRealm(id);
    setStage('character-roster');
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-[#0B0907]"
      style={{ fontFamily: "'EB Garamond', Georgia, serif" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url('${import.meta.env.BASE_URL}brand/splash-bg.png')`, filter: 'blur(3px) saturate(0.85)' }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_25%,rgba(11,9,7,0.95)_100%)]" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {/* Header */}
        <header className="text-center mb-10">
          <p
            className="text-[10px] tracking-[0.5em] uppercase text-[#9C8E72] mb-3"
            style={{ fontFamily: "'Spectral SC', serif" }}
          >
            Выбери свой мир
          </p>
          <h1
            className="text-4xl sm:text-6xl text-[#E8C77B] mb-3"
            style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: '0.08em' }}
          >
            Реалмы AIWARI
          </h1>
          <p
            className="text-[#C4B391] italic max-w-2xl mx-auto text-base sm:text-lg"
            style={{ fontFamily: "'EB Garamond', serif" }}
          >
            Каждый реалм — отдельный мир Стормвейла со своим темпом, правилами и сообществом.
            Персонажи между реалмами не переносятся.
          </p>
        </header>

        {/* Filigree divider */}
        <div className="flex items-center justify-center mb-10 select-none" aria-hidden>
          <span className="h-px flex-1 max-w-[200px] bg-gradient-to-r from-transparent via-[#8A6B2E] to-[#C9A96A]" />
          <span className="mx-4 text-[#C9A96A] text-lg">⚔</span>
          <span className="h-px flex-1 max-w-[200px] bg-gradient-to-l from-transparent via-[#8A6B2E] to-[#C9A96A]" />
        </div>

        {/* Realm cards — items-stretch + h-full на ребёнке выравнивает все 3 карточки в одну высоту */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-7 items-stretch">
          {REALMS.map((realm) => (
            <RealmCard
              key={realm.id}
              realm={realm}
              isCurrent={currentRealm === realm.id}
              online={onlineByRealm[realm.id] ?? 0}
              onSelect={() => onSelect(realm.id)}
            />
          ))}
        </div>

        {/* R232q69: total online + mode disclaimer */}
        <div className="mt-8 text-center">
          <div
            className="inline-flex items-center gap-3 px-5 py-2 border border-[#5C4520] bg-[#0F0B07]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#9C8E72]">всего онлайн</span>
            <span className="text-2xl text-[#5BAE57] font-bold">{totalOnline.toLocaleString('ru-RU')}</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#9C8E72]">персонажей</span>
          </div>
        </div>

        <div
          className="mt-10 max-w-3xl mx-auto px-5 py-6 border border-[#3A2818] bg-[#100C08] rounded-sm"
          style={{ fontFamily: "'EB Garamond', serif" }}
        >
          <h3
            className="text-[12px] tracking-[0.4em] uppercase text-[#E8C77B] mb-4 text-center"
            style={{ fontFamily: "'Spectral SC', serif" }}
          >
            ✦ Что такое реалмы и режимы ✦
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-[13px] leading-relaxed">
            <div>
              <div className="text-[#5BAE57] font-bold uppercase tracking-wider text-[11px] mb-1">PvE</div>
              <p className="text-[#C4B391]">
                «Player vs Environment» — игрок против мира. Сражения только с монстрами и боссами,
                другие игроки атаковать тебя не могут. Спокойный темп, фокус на квестах и подземельях.
              </p>
            </div>
            <div>
              <div className="text-[#F87171] font-bold uppercase tracking-wider text-[11px] mb-1">PvP/PvE</div>
              <p className="text-[#C4B391]">
                «Player vs Player» — игроки могут сражаться друг с другом в спорных зонах.
                Двойной опыт за всё. Подойдёт тем, кто хочет открытые сражения и ускоренную прокачку.
              </p>
            </div>
            <div>
              <div className="text-[#C9A96A] font-bold uppercase tracking-wider text-[11px] mb-1">Hardcore</div>
              <p className="text-[#C4B391]">
                Один шанс. Если персонаж умирает — он покидает мир навсегда (в списке героев остаётся,
                но играть им больше нельзя). Реалм для тех, кто играет вдумчиво и осторожно.
              </p>
            </div>
          </div>
          <p
            className="text-[12px] text-[#9C8E72] italic text-center mt-5"
            style={{ fontFamily: "'EB Garamond', serif" }}
          >
            «Множитель» — во сколько раз быстрее накапливается опыт и репутация. ×1 — обычная скорость, ×2 — вдвое быстрее.
          </p>
        </div>

        {/* R232q71: back-link убран — splash больше нет, переход в auth ниже как logout. */}
      </div>
    </div>
  );
}

function RealmCard({
  realm,
  isCurrent,
  online,
  onSelect,
}: {
  realm: RealmConfig;
  isCurrent: boolean;
  online: number;
  onSelect: () => void;
}) {
  const isLive = realm.status === 'live';
  const isSoon = realm.status === 'soon';
  const isReserved = realm.status === 'reserved';

  const accentColor =
    realm.type === 'PvE' ? '#3E6FA5' :
    realm.type === 'PvP/PvE' ? '#8B2828' :
    '#7C6B3A';

  return (
    <article
      className={`relative bg-[#161210] border transition-all duration-300 flex flex-col h-full ${
        isLive
          ? 'border-[#5C4520] hover:border-[#C9A96A] cursor-pointer hover:-translate-y-1'
          : 'border-[#3A2818] opacity-70 cursor-not-allowed'
      } ${isCurrent ? 'border-[#E8C77B] shadow-[0_0_24px_rgba(232,199,123,0.3)]' : ''}`}
      onClick={isLive ? onSelect : undefined}
    >
      {/* Top accent strip */}
      <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />

      {/* R232q30: Gemini-generated per-realm hero art (1024x1024 letterboxed). */}
      <div
        className="relative h-40 sm:h-44 bg-cover bg-center"
        style={{
          backgroundImage: `url('${import.meta.env.BASE_URL}brand/realm-${realm.id === 'eldran' ? 'eldran' : realm.id === 'sharpcrest' ? 'grozovoy' : 'bessmertnik'}.png')`,
        }}
        aria-hidden
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,9,7,0.15) 0%, rgba(11,9,7,0.45) 60%, #161210 100%)' }} />
        {/* R232q71: large always-visible online counter — top-right corner badge на hero art. */}
        <div
          className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-md"
          style={{
            background: 'rgba(11,9,7,0.85)',
            border: '1px solid #5C4520',
            backdropFilter: 'blur(4px)',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <span className="w-2 h-2 rounded-full bg-[#5BAE57] animate-pulse" />
          <span className="text-[#5BAE57] font-bold text-base leading-none">{online.toLocaleString('ru-RU')}</span>
          <span className="text-[9px] uppercase tracking-[0.2em] text-[#9C8E72] leading-none">онлайн</span>
        </div>
      </div>

      <div className="relative p-6 sm:p-8 flex-1 flex flex-col">
        <CornerOrnament size={56} />
        <div style={{ position: 'absolute', top: 0, right: 0 }}>
          <CornerOrnament size={56} flipX />
        </div>

        {/* Status badge */}
        <div className="mb-4 flex items-center justify-between">
          <span
            className="text-[10px] tracking-[0.35em] uppercase"
            style={{
              fontFamily: "'Spectral SC', serif",
              color: isLive ? '#5BAE57' : isSoon ? '#D9A441' : '#5C4520',
            }}
          >
            {isLive && '● Онлайн'}
            {isSoon && '◯ Скоро'}
            {isReserved && '○ Зарезервирован'}
          </span>
          {isCurrent && (
            <span
              className="text-[10px] tracking-[0.3em] uppercase text-[#E8C77B]"
              style={{ fontFamily: "'Spectral SC', serif" }}
            >
              ✦ Текущий
            </span>
          )}
        </div>

        {/* Realm name + rate — h-[88px] для одинаковой высоты на всех 3 картах */}
        <div className="mb-4 min-h-[88px] sm:min-h-[100px]">
          <h2
            className="text-2xl sm:text-3xl text-[#E8C77B] mb-1 leading-tight"
            style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: '0.05em' }}
          >
            {realm.name}
          </h2>
          <div
            className="flex items-baseline gap-3"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            <span className="text-2xl text-[#F2D58A] font-bold">{realm.rate}</span>
            <span className="text-sm tracking-[0.3em] uppercase" style={{ color: accentColor }}>
              {realm.type}
            </span>
          </div>
        </div>

        {/* Description — фиксированная высота для выравнивания, flex-1 чтобы занять весь свободный slot */}
        <p
          className="text-[#C4B391] italic leading-relaxed mb-5 flex-1"
          style={{ fontFamily: "'EB Garamond', serif", minHeight: '5rem' }}
        >
          {realm.description}
        </p>

        {/* R232q69: stats panel рендерится для ВСЕХ realms (раньше только isLive) — одинаковая высота карточек */}
        <div
          className={`text-xs px-4 py-3 bg-[#0B0907] border border-[#3A2818]/80 mb-5 ${isLive ? '' : 'opacity-60'}`}
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          <div className="flex justify-between text-[#9C8E72]">
            <span>множитель</span>
            <span className="text-[#E8C77B]">×{realm.rate.replace('X', '')}</span>
          </div>
          <div className="flex justify-between text-[#9C8E72] mt-1">
            <span>тип</span>
            <span className="text-[#E8C77B]">{realm.type}</span>
          </div>
          {/* R232q71: онлайн дублируется в badge поверх hero art — здесь убран. */}
        </div>

        {/* CTA */}
        <button
          type="button"
          disabled={!isLive}
          onClick={isLive ? onSelect : undefined}
          className={`w-full px-5 py-3 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
            isLive
              ? 'border-[#5C4520] hover:border-[#C9A96A] hover:text-[#F2D58A] hover:bg-[#1F1813] text-[#E8DCC0] cursor-pointer'
              : 'border-[#3A2818] text-[#5C4520] cursor-not-allowed'
          }`}
          style={{ fontFamily: "'Cinzel', serif" }}
        >
          {isLive && '⚔ Войти в реалм'}
          {isSoon && 'Появится позже'}
          {isReserved && 'В разработке'}
        </button>
      </div>
    </article>
  );
}
